import React, { useEffect, useState, useRef } from 'react';
import { Icon, Switch, DatePicker, TimePicker, Row, Col, Select } from 'antd';
import moment from 'moment';
import * as S from '../styles';
import { useBatchJob } from '../../../../../context/BatchJobContext';
import { useTranslation } from 'react-i18next';
import { BATCH_JOBS_DASHBOARD as trans } from '../translationValues';
import { getUser } from '../../../../../utils/services/userAPI';
import { batchSchedule, saveBatchConfig } from '../../../../../utils/services/statusMonitoringAPI';

const NAMESPACE = ['BATCH_JOBS_DASHBOARD'];

const Step5Scheduling = () => {
  const {
    jobType,
    scheduleEnabled,
    setScheduleEnabled,
    scheduleDate,
    setScheduleDate,
    scheduleTime,
    setScheduleTime,
    approvalEnabled,
    setApprovalEnabled,
    approvalType,
    setApprovalType,
    approvalLevel,
    setApprovalLevel,
    selectedApprover,
    setSelectedApprover,
    batchId,
    approvers,
    autoTrigger,
    setAutotrigger
  } = useBatchJob();

  const { t } = useTranslation(NAMESPACE);

  const handleSelectUser = val => {
    setSelectedApprover(val);
    setApprovalType('USER');
  };

  const disablePastDates = (current) => {
    return current && current < moment().startOf('day');
  };

  const handleChange = (date, dateString) => {
    const formattedDate = new Date(date).toISOString().split('T')[0];
    setScheduleDate(dateString);
    console.log(formattedDate);
    // Output: 2026-02-23
  }

  const handleAutoTrigger = (type) =>{
    setAutotrigger(type ==='auto'? true : false);
  }
 

  const initialRef = useRef(true);

  useEffect(() => {
    const saveInfo = async () => {
      let formattedDateAndTime = null;
      if(scheduleEnabled && scheduleDate && scheduleTime) {
        const utcTime = moment(scheduleTime, "HH:mm:ss")
          .utc().format("HH:mm:ss");
      formattedDateAndTime = `${scheduleDate}T${utcTime}Z`;
      }
       
      const payload = {
        batchId,
        processingType: scheduleEnabled ? 'SCHEDULED' : 'IMMEDIATE',
        ...(formattedDateAndTime && { scheduledTime: formattedDateAndTime }),
        requiresApproval: approvalEnabled,
        ...(approvalEnabled && { approvalType }),
        ...(approvalEnabled && {
          approver: approvalType === 'LEVEL' ? approvalLevel : selectedApprover,
        }),
      };
      try {
        await saveBatchConfig(payload);
      } catch (e) {
        console.error(e);
      }
    };
    if (!initialRef.current && jobType === 'ADVANCED') {
      // saveInfo(); TODO: Enable once the backend toggle issue is fixed
    } else {
      initialRef.current = false;
    }
  }, [
    scheduleEnabled,
    scheduleDate,
    scheduleTime,
    approvalEnabled,
    approvalType,
    approvalLevel,
    selectedApprover,
  ]);

  return (
    <S.StepContent>
      <S.FormContainer>
        <S.StepHeader>
          <S.StepHeaderTitle>
            {t(trans.SCHEDULE_CAMPAIGN_TITLE.key, trans.SCHEDULE_CAMPAIGN_TITLE.value)}
          </S.StepHeaderTitle>
          <S.StepSubtitle>
            {t(trans.SCHEDULE_CAMPAIGN_SUBTITLE.key, trans.SCHEDULE_CAMPAIGN_SUBTITLE.value)}
          </S.StepSubtitle>
        </S.StepHeader>
        <S.ActionCard>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <S.IconBox color="#927BF8">
              <Icon type="calendar" style={{ fontSize: '20px' }} />
            </S.IconBox>
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px' }}>
                {t(trans.ENABLE_SCHEDULING.key, trans.ENABLE_SCHEDULING.value)}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {t(trans.SCHEDULE_DESC.key, trans.SCHEDULE_DESC.value)}
              </div>
            </div>
          </div>
          <Switch className="switch" checked={scheduleEnabled} onChange={setScheduleEnabled} />
        </S.ActionCard>

        {!scheduleEnabled && (
          <S.SendContainer>
          <S.ImmediateCard onClick={()=> setAutotrigger(true)} enabled={autoTrigger}>
            <S.BoltIcon enabled={autoTrigger}>
              <Icon type="thunderbolt"/>
            </S.BoltIcon>
            <div style={{ fontWeight: '800', fontSize: '18px', color: '#1f2937' }}>
              {t(trans.SEND_IMMEDIATELY.key, trans.SEND_IMMEDIATELY.value)}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {t(trans.SEND_IMMEDIATELY_DESC.key, trans.SEND_IMMEDIATELY_DESC.value)}
            </div>
          </S.ImmediateCard>
          <S.ImmediateCard onClick={()=> setAutotrigger(false)} enabled={!autoTrigger}>
            <S.BoltIcon enabled={!autoTrigger}>
              <Icon type="file-markdown"/>
            </S.BoltIcon>
            <div style={{ fontWeight: '800', fontSize: '18px', color: '#1f2937' }}>
            {t(trans.SEND_MANUALLY.key, trans.SEND_MANUALLY.value)}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {t(trans.SEND_MANUALLY_DESC.key, trans.SEND_MANUALLY_DESC.value)}
            </div>
          </S.ImmediateCard>
          </S.SendContainer>
        )}

        {scheduleEnabled && (
          <S.ConfigSection style={{ marginBottom: '24px' }}>
            <div
              style={{
                fontWeight: '600',
                fontSize: '0.875rem',
                color: '#1f2937',
                marginBottom: '1rem',
              }}
            >
              {t(trans.DATE_TIME_TO_SCHEDULE.key, trans.DATE_TIME_TO_SCHEDULE.value)}
            </div>
            <Row gutter={24}>
              <Col span={12}>
                <S.Label>
                  {t(trans.SELECT_DATE.key, trans.SELECT_DATE.value)} <S.Required>*</S.Required>
                </S.Label>
                <S.StyledDatePicker
                  style={{ width: '100%' }}
                  value={scheduleDate ? moment(scheduleDate) : null}
                  onChange={handleChange}
                  getCalendarContainer={trigger => trigger.parentNode}
                  disabledDate={disablePastDates}
                />
              </Col>
              <Col span={12}>
                <S.Label>
                  {t(trans.SELECT_TIME.key, trans.SELECT_TIME.value)} <S.Required>*</S.Required>
                </S.Label>
                <S.StyledTimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  value={scheduleTime ? moment(scheduleTime, 'HH:mm') : null}
                  onChange={(t, ts) => setScheduleTime(ts)}
                  getPopupContainer={trigger => trigger.parentNode}
                  disabled={!scheduleDate}
                  disabledHours={() => {
                    const now = moment();
                    const selectedDate = scheduleDate ? moment(scheduleDate) : null;
                    if (selectedDate && selectedDate.isSame(now, 'day')) {
                      return Array.from({ length: now.hour() }, (_, i) => i);
                    }
                    return [];
                  }}
                  disabledMinutes={selectedHour => {
                    const now = moment();
                    const selectedDate = scheduleDate ? moment(scheduleDate) : null;
                    if (selectedDate && selectedDate.isSame(now, 'day') && selectedHour === now.hour()) {
                      return Array.from({ length: now.minute() + 1 }, (_, i) => i);
                    }
                    return [];
                  }}
                />
              </Col>
            </Row>
          </S.ConfigSection>
        )}

        <S.ActionCard>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <S.IconBox color="#927BF8">
              <Icon type="check-circle" style={{ fontSize: '20px' }} />
            </S.IconBox>
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px' }}>
                {t(trans.ENABLE_APPROVAL.key, trans.ENABLE_APPROVAL.value)}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {t(trans.APPROVAL_DESC.key, trans.APPROVAL_DESC.value)}
              </div>
            </div>
          </div>
          <Switch checked={approvalEnabled} onChange={setApprovalEnabled} />
        </S.ActionCard>

        {approvalEnabled && (
          <S.ConfigSection>
            <S.TabGroup>
              <S.Tab active={approvalType === 'LEVEL'} onClick={() => { setApprovalType('LEVEL'); setSelectedApprover(null); setApprovalLevel(null); }}>
                <Icon type="team" /> {t(trans.APPROVAL_LEVEL.key, trans.APPROVAL_LEVEL.value)}
              </S.Tab>
              <S.Tab active={approvalType === 'USER'} onClick={() => { setApprovalType('USER'); setSelectedApprover(null); setApprovalLevel(null); }}>
                <Icon type="user" /> {t(trans.SPECIFIC_USER.key, trans.SPECIFIC_USER.value)}
              </S.Tab>
            </S.TabGroup>

            {approvalType === 'LEVEL' && (
              <div>
                <S.Label style={{ fontSize: '15px' }}>
                  {t(trans.SELECT_APPROVAL_LEVEL.key, trans.SELECT_APPROVAL_LEVEL.value)}{' '}
                  <S.Required>*</S.Required>
                </S.Label>
                <S.LevelGrid>
                  {['1', '2', '3', '4'].map(lvl => (
                    <S.LevelButton
                      key={lvl}
                      selected={approvalLevel === lvl}
                      onClick={() => setApprovalLevel(lvl)}
                    >
                      {t(trans.LEVEL_LABEL.key, {
                        level: lvl,
                        defaultValue: trans.LEVEL_LABEL.value,
                      })}
                    </S.LevelButton>
                  ))}
                </S.LevelGrid>
                <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                  {t(trans.APPROVAL_LEVEL_REQUIRED_DESC.key, {
                    level: approvalLevel,
                    defaultValue: trans.APPROVAL_LEVEL_REQUIRED_DESC.value,
                  })}
                </div>
              </div>
            )}
            {approvalType === 'USER' && (
              <div>
                <S.Label style={{ fontSize: '15px' }}>
                  {t(trans.SELECT_APPROVER.key, trans.SELECT_APPROVER.value)}{' '}
                  <S.Required>*</S.Required>
                </S.Label>
                <Select
                  showSearch
                  size="large"
                  placeholder={t(
                    trans.SEARCH_USER_PLACEHOLDER.key,
                    trans.SEARCH_USER_PLACEHOLDER.value,
                  )}
                  value={selectedApprover ? String(selectedApprover) : undefined}
                  onChange={handleSelectUser}
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                  dropdownStyle={{ zIndex: 10001 }}
                  filterOption={(input, option) => {
                    const children = option.props.children;

                    const label = Array.isArray(children) ? children.join('') : children;

                    return String(label)
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                >
                  {approvers.map(a => {
                    const userName = a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim();
                    return (
                      <Select.Option key={a.id} value={String(a.id)}>
                        {userName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            )}
          </S.ConfigSection>
        )}
      </S.FormContainer>
    </S.StepContent>
  );
};

export default Step5Scheduling;
